import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import {
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
} from "lucide-react";
import type { ContractResponseDTO } from "../types/contract.types";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { memo } from "react";

/**
 * Tabela paginada de contratos com ações de editar, excluir e visualizar imagem.
 *
 * A paginação usa índice 0-based (alinhado com a API). Ações de editar e excluir
 * são protegidas por `<Can>` e só renderizadas para roles com a permissão adequada.
 * O ellipsis na paginação aparece quando há mais de 5 páginas e a página atual
 * está distante do início ou fim.
 */
interface ContractsTableProps {
  contracts: ContractResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (contract: ContractResponseDTO) => void;
  onDelete: (contract: ContractResponseDTO) => void;
  onViewImage: (contract: ContractResponseDTO) => void;
  onPageChange: (page: number) => void;
}

export const ContractsTable = memo(function ContractsTable({
  contracts,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onViewImage,
  onPageChange,
}: ContractsTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nº do Contrato</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Contrato Pai</TableHead>
            <TableHead>Imagem</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contracts.map((contract) => (
            <TableRow key={contract.id}>
              <TableCell className="font-medium">
                {contract.contractNumber}
              </TableCell>
              <TableCell>{contract.clientName}</TableCell>
              <TableCell className="text-muted-foreground">
                {contract.parentingContractNumber ?? "—"}
              </TableCell>
              <TableCell>
                {contract.contractImageUrl ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onViewImage(contract)}
                    title="Ver imagem"
                    disabled={loading}
                  >
                    <ImageIcon className="w-4 h-4 text-primary" />
                  </Button>
                ) : (
                  <span className="text-muted-foreground text-sm">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Can permission={ACTIONS.contracts.update}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onEdit(contract)}
                      title="Editar"
                      disabled={loading}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Can>
                  <Can permission={ACTIONS.contracts.delete}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onDelete(contract)}
                      title="Excluir"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </Can>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
